import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, IconButton, Typography, Slider, Stack, CircularProgress } from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  FullscreenExit,
  CameraAlt,
  MusicNote,
} from '@mui/icons-material';
import ApiService from '../../services/api-service';
import { getUserId } from '../../constants/function';
import notifier from "../../components/Notifier";

const SONG_COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4'];

const VideoPlayerEnhanced = ({
  video,
  studioId,
  musicData,
  musicTracks,
  onTimeUpdate,
  onLoadedMetadata,
  onAspectRatioDetected,
  style = {}
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const streamingActiveRef = useRef(null);
  const prevActiveSongRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [videoAspectRatio, setVideoAspectRatio] = useState(null);
  const [isVertical, setIsVertical] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [savingThumbnail, setSavingThumbnail] = useState(false);
  const [thumbnailStatus, setThumbnailStatus] = useState('');

  // Compute which song is currently playing based on video position
  const activeSong = useMemo(() => {
    if (!musicTracks?.length || duration <= 0) return null;
    return musicTracks.find(t =>
      currentTime >= t.startTime && (t.endTime == null || currentTime < t.endTime)
    );
  }, [musicTracks, currentTime, duration]);

  const activeIndex = useMemo(() => {
    if (!musicTracks?.length || !activeSong) return -1;
    return musicTracks.indexOf(activeSong);
  }, [musicTracks, activeSong]);

  // Use active song for overlay/streaming, fall back to first song
  const currentMusicData = activeSong || musicData;

  // Compute rail gradient for song markers
  const railBackground = useMemo(() => {
    if (!musicTracks?.length || duration <= 0) return null;
    const sorted = [...musicTracks].sort((a, b) => a.startTime - b.startTime);
    const stops = [];
    let lastPct = 0;

    for (let i = 0; i < sorted.length; i++) {
      const startPct = Math.max(0, (sorted[i].startTime / duration) * 100);
      const endPct = Math.min(100, ((sorted[i].endTime ?? duration) / duration) * 100);
      const color = SONG_COLORS[i % SONG_COLORS.length];

      if (startPct > lastPct) {
        stops.push(`rgba(255,255,255,0.15) ${lastPct}%, rgba(255,255,255,0.15) ${startPct}%`);
      }
      stops.push(`${color}99 ${startPct}%, ${color}99 ${endPct}%`);
      lastPct = endPct;
    }

    if (lastPct < 100) {
      stops.push(`rgba(255,255,255,0.15) ${lastPct}%, rgba(255,255,255,0.15) 100%`);
    }
    return `linear-gradient(to right, ${stops.join(', ')})`;
  }, [musicTracks, duration]);

  // Reset streaming ref when video changes
  useEffect(() => {
    streamingActiveRef.current = null;
    prevActiveSongRef.current = null;
  }, [video?._id]);

  // Handle song transitions during playback (royalty tracking)
  useEffect(() => {
    if (!isPlaying) return;
    const newId = activeSong?._id;
    const prevId = prevActiveSongRef.current;
    if (newId !== prevId) {
      if (prevId && streamingActiveRef.current === prevId) {
        ApiService.STREAM_STOP({ musicId: prevId }).catch(() => {});
        streamingActiveRef.current = null;
      }
      if (newId && streamingActiveRef.current !== newId) {
        ApiService.STREAM_START({ musicId: newId }).catch(() => {});
        streamingActiveRef.current = newId;
      }
      prevActiveSongRef.current = newId;
    }
  }, [activeSong?._id, isPlaying]);

  const handleStartMusicStreaming = async () => {
    const target = currentMusicData;
    if (!target?._id || streamingActiveRef.current === target._id) return;
    try {
      await ApiService.STREAM_START({ musicId: target._id });
      streamingActiveRef.current = target._id;
      prevActiveSongRef.current = target._id;
    } catch (err) {
      console.error('[VideoPlayerEnhanced] Failed to start streaming:', err);
    }
  };

  const handleStopMusicStreaming = async () => {
    const activeId = streamingActiveRef.current;
    if (!activeId) return;
    streamingActiveRef.current = null;
    prevActiveSongRef.current = null;
    try {
      await ApiService.STREAM_STOP({ musicId: activeId });
    } catch (err) {
      console.error('[VideoPlayerEnhanced] Failed to stop streaming:', err);
    }
  };

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const updateTime = () => {
      const t = videoElement.currentTime;
      setCurrentTime(t);
      if (onTimeUpdate) onTimeUpdate(t);
    };
    const updateDuration = () => setDuration(videoElement.duration);
    const handleEnded = () => { handleStopMusicStreaming(); setIsPlaying(false); };

    videoElement.addEventListener('timeupdate', updateTime);
    videoElement.addEventListener('loadedmetadata', updateDuration);
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('ended', handleEnded);

    return () => {
      videoElement.removeEventListener('timeupdate', updateTime);
      videoElement.removeEventListener('loadedmetadata', updateDuration);
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [video?._id]);

  useEffect(() => {
    // Check ownership when video changes
    if (video) {
      checkOwnership();
    }
  }, [video?._id]);

  const checkOwnership = () => {
    const currentUserId = getUserId();

    if (!currentUserId || !video) {
      setIsOwner(false);
      return;
    }

    const uploaderIds = [
      video.uploader?._id,
      video.uploader?.id,
      video.uploader, // Direct string ID
      video.createdBy,
      video.uploadedBy,
      video.uploaderID,
      video.uploaderId,
      video.ownerId,
      video.userId,
      video.studioId,
      studioId // Also check the passed studioId
    ].filter(Boolean);

    const isVideoOwner = uploaderIds.some(id =>
      String(currentUserId) === String(id)
    );
    const isStudioOwner = String(currentUserId) === String(video.studioId);
    const isStudioOwnerByUserId = String(currentUserId) === String(studioId);

    setIsOwner(isVideoOwner || isStudioOwner || isStudioOwnerByUserId);
  };

  const handleLoadedMetadata = (e) => {
    const video = e.target;
    const aspectRatio = video.videoWidth / video.videoHeight;
    setVideoAspectRatio(aspectRatio);
    setIsVertical(aspectRatio < 1);

    if (onLoadedMetadata) {
      onLoadedMetadata(e);
    }

    if (onAspectRatioDetected) {
      onAspectRatioDetected(aspectRatio);
    }
  };

  const togglePlay = () => {
    const videoElement = videoRef.current;
    if (isPlaying) {
      videoElement.pause();
      handleStopMusicStreaming();
    } else {
      videoElement.play();
      handleStartMusicStreaming();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (event, newValue) => {
    const videoElement = videoRef.current;
    videoElement.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const handleVolumeChange = (event, newValue) => {
    const videoElement = videoRef.current;
    videoElement.volume = newValue;
    setVolume(newValue);
    setIsMuted(newValue === 0);
  };

  const toggleMute = () => {
    const videoElement = videoRef.current;
    if (isMuted) {
      videoElement.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      videoElement.volume = 0;
      setIsMuted(true);
    }
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;

    if (!isFullscreen) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.webkitEnterFullscreen) {
        container.webkitEnterFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const captureCurrentFrame = async () => {
    if (!videoRef.current || !isOwner) return;

    setSavingThumbnail(true);
    setThumbnailStatus('Capturing frame...');

    try {
      const videoElement = videoRef.current;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });

      const formData = new FormData();
      formData.append('thumbnail', blob, 'thumbnail.jpg');

      if (video?.title) {
        formData.append('title', video.title);
      }
      if (video?.description) {
        formData.append('description', video.description);
      }

      const currentUserId = getUserId();
      const videoStudioId = studioId || video?.studioId || currentUserId;
      const videoId = video?._id;

      if (!videoStudioId || !videoId) {
        console.error('Cannot determine studio ID or video ID');
        return;
      }

      setThumbnailStatus('Uploading thumbnail...');

      const response = await ApiService.UPDATEVIDEO({
        studioId: videoStudioId,
        videoId: videoId,
        formData: formData
      });

      if (response.status === 'success' || response.success || response.ok) {
        setThumbnailStatus('Thumbnail saved!');
        notifier.success('Thumbnail updated successfully! The page will now reload to show your new thumbnail.');
        setTimeout(() => {
          window.location.reload(true);
        }, 1000);
      } else {
        console.error('Failed to update thumbnail:', response);
        setThumbnailStatus('Failed to update thumbnail');
        setTimeout(() => {
          setThumbnailStatus('');
        }, 3000);
      }

    } catch (error) {
      console.error('Error capturing frame:', error);
      setThumbnailStatus('Error capturing frame');
      setTimeout(() => {
        setThumbnailStatus('');
        setSavingThumbnail(false);
      }, 3000);
    }
  };

  const getContainerStyle = () => {
    if (isVertical) {
      return {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--background-color, #000)',
        ...style
      };
    }
    return {
      width: '100%',
      height: '100%',
      position: 'relative',
      backgroundColor: 'var(--background-color, #000)',
      ...style
    };
  };

  const getVideoStyle = () => {
    if (isVertical) {
      return {
        height: '100%',
        width: 'auto',
        maxWidth: '100%',
        objectFit: 'contain'
      };
    }
    return {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    };
  };

  return (
    <Box
      ref={containerRef}
      sx={getContainerStyle()}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
      position="relative"
    >
      <video
        ref={videoRef}
        src={video?.url || video}
        style={getVideoStyle()}
        onClick={togglePlay}
        playsInline
        webkit-playsinline="true"
        x-webkit-airplay="allow"
        disablePictureInPicture
      />

      {/* Song Cover Overlay - bottom right, shows currently playing song */}
      {currentMusicData?.thumbnailImg?.filePath && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 72,
            right: 12,
            width: 48,
            height: 48,
            borderRadius: 1,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.6)',
            border: `2px solid ${activeIndex >= 0 ? SONG_COLORS[activeIndex % SONG_COLORS.length] : 'rgba(255,255,255,0.15)'}`,
            zIndex: 2,
            opacity: showControls ? 1 : 0,
            transition: 'opacity 0.3s, border-color 0.3s',
          }}
        >
          <img
            src={currentMusicData.thumbnailImg.filePath}
            alt={currentMusicData.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </Box>
      )}

      {/* Video Controls Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
          pt: 4,
          px: 2,
          pb: 1,
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.3s',
        }}
      >
        {/* Active song name label */}
        {activeSong && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <MusicNote sx={{ fontSize: 12, color: SONG_COLORS[activeIndex % SONG_COLORS.length] }} />
            <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px', fontWeight: 500 }}>
              {activeSong.title}{activeSong.artist ? ` \u2014 ${activeSong.artist}` : ''}
            </Typography>
          </Box>
        )}

        {/* Progress Bar with song markers */}
        <Slider
          value={currentTime}
          max={duration || 1}
          onChange={handleSeek}
          sx={{
            color: '#ffee20',
            height: 3,
            padding: '4px 0',
            '& .MuiSlider-thumb': {
              width: 10,
              height: 10,
              transition: 'width 0.15s, height 0.15s',
              '&:hover, &.Mui-focusVisible': { width: 14, height: 14 },
            },
            '& .MuiSlider-rail': {
              background: railBackground || 'rgba(255,255,255,0.2)',
              opacity: 1,
            },
          }}
        />

        {/* Control Buttons */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: -0.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton onClick={togglePlay} size="small" sx={{ color: '#fff' }}>
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>

            <IconButton onClick={toggleMute} size="small" sx={{ color: '#fff' }}>
              {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
            </IconButton>
            <Slider
              value={isMuted ? 0 : volume}
              max={1}
              step={0.1}
              onChange={handleVolumeChange}
              sx={{
                width: 80,
                color: '#fff',
                '& .MuiSlider-thumb': { width: 10, height: 10 },
              }}
            />

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', ml: 1, fontSize: '0.7rem' }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            {/* Capture Thumbnail — owner only */}
            {isOwner && (
              <IconButton
                onClick={captureCurrentFrame}
                disabled={savingThumbnail}
                size="small"
                sx={{ color: '#fff', '&:disabled': { color: '#666' } }}
                title="Save current frame as thumbnail"
              >
                {savingThumbnail ? (
                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                ) : (
                  <CameraAlt fontSize="small" />
                )}
              </IconButton>
            )}

            <IconButton onClick={toggleFullscreen} size="small" sx={{ color: '#fff' }}>
              {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Thumbnail Capture Status */}
      {thumbnailStatus && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: '#fff',
            px: 3,
            py: 2,
            borderRadius: 2,
            fontSize: '16px',
            fontWeight: 'bold',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            zIndex: 9999,
          }}
        >
          {thumbnailStatus}
        </Box>
      )}
    </Box>
  );
};

export default VideoPlayerEnhanced;
